import { useState, useEffect } from "react";
import { toast } from "sonner";
import {
  CreditCard,
  Plus,
  Trash2,
  CheckCircle2,
  AlertOctagon,
  RefreshCw,
  Lock,
  Unlock,
  Pencil,
  X,
  Check,
  History,
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { getTerminals, createTerminal, updateTerminal, deleteTerminal } from "@/lib/api";

export const TerminalsDialog = ({ open, onOpenChange, onTerminalsUpdated }) => {
  const [terminals, setTerminals] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);

  // New terminal state
  const [name, setName] = useState("");
  const [notes, setNotes] = useState("");
  const [status, setStatus] = useState("activa");

  // Edit terminal state
  const [editingTerminal, setEditingTerminal] = useState(null);
  const [editName, setEditName] = useState("");
  const [editStatus, setEditStatus] = useState("activa");
  const [editNotes, setEditNotes] = useState("");

  const loadTerminals = async () => {
    try {
      setLoading(true);
      const data = await getTerminals();
      setTerminals(data || []);
      onTerminalsUpdated?.(data || []);
    } catch {
      toast.error("Error al cargar las terminales");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) {
      loadTerminals();
      setEditingTerminal(null);
    }
  }, [open]);

  const handleCreate = async (e) => {
    e.preventDefault();
    const cleanName = name.trim();
    if (!cleanName) {
      toast.error("Ingresa un nombre para la terminal");
      return;
    }
    try {
      setSubmitting(true);
      const created = await createTerminal({
        name: cleanName,
        status,
        notes: notes.trim(),
      });
      toast.success(`Terminal "${created.name}" creada con éxito`);
      setName("");
      setNotes("");
      setStatus("activa");
      setShowAddForm(false);
      await loadTerminals();
    } catch (err) {
      const msg = err.response?.data?.detail || "No se pudo crear la terminal";
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const startEdit = (term) => {
    setEditingTerminal(term);
    setEditName(term.name);
    setEditStatus(term.status);
    setEditNotes(term.notes || "");
    setShowAddForm(false);
  };

  const cancelEdit = () => {
    setEditingTerminal(null);
    setEditName("");
    setEditStatus("activa");
    setEditNotes("");
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    if (!editingTerminal) return;
    const cleanName = editName.trim();
    if (!cleanName) {
      toast.error("El nombre no puede estar vacío");
      return;
    }
    try {
      setSubmitting(true);
      await updateTerminal(editingTerminal.id, {
        name: cleanName,
        status: editStatus,
        notes: editNotes.trim(),
      });
      toast.success(`Terminal "${cleanName}" actualizada`);
      cancelEdit();
      await loadTerminals();
    } catch (err) {
      const msg = err.response?.data?.detail || "No se pudo actualizar la terminal";
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const handleToggleStatus = async (term) => {
    const nextStatus = term.status === "activa" ? "bloqueada" : "activa";
    try {
      await updateTerminal(term.id, { status: nextStatus });
      toast.success(`Terminal "${term.name}" ahora está ${nextStatus.toUpperCase()}`);
      await loadTerminals();
    } catch {
      toast.error("Error al actualizar estado de la terminal");
    }
  };

  const handleDelete = async (term) => {
    try {
      await deleteTerminal(term.id);
      toast.success(`Terminal "${term.name}" eliminada del catálogo. Los leads conservan su historial.`);
      if (editingTerminal?.id === term.id) {
        cancelEdit();
      }
      await loadTerminals();
    } catch {
      toast.error("Error al eliminar la terminal");
    }
  };

  const activasCount = terminals.filter((t) => t.status === "activa").length;
  const bloqueadasCount = terminals.filter((t) => t.status === "bloqueada").length;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] flex flex-col p-6" data-testid="terminals-modal">
        <DialogHeader className="pb-3 border-b border-slate-100">
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-2.5">
              <div className="w-10 h-10 rounded-xl bg-blue-50 text-blue-600 flex items-center justify-center border border-blue-100/60 shadow-sm">
                <CreditCard className="w-5 h-5" />
              </div>
              <div>
                <DialogTitle className="font-heading text-xl text-slate-800">
                  Módulo de Terminales
                </DialogTitle>
                <DialogDescription className="text-xs text-slate-500">
                  Crea, edita o elimina terminales. Los cobros ya registrados conservan siempre su historial.
                </DialogDescription>
              </div>
            </div>

            {/* Botón Agregar Terminal */}
            <Button
              type="button"
              size="sm"
              onClick={() => {
                cancelEdit();
                setShowAddForm(!showAddForm);
              }}
              className={`text-xs font-semibold ${
                showAddForm
                  ? "bg-slate-200 text-slate-700 hover:bg-slate-300"
                  : "bg-blue-600 hover:bg-blue-700 text-white"
              }`}
              data-testid="add-terminal-button"
            >
              {showAddForm ? (
                <>
                  <X className="w-3.5 h-3.5 mr-1" /> Cancelar
                </>
              ) : (
                <>
                  <Plus className="w-3.5 h-3.5 mr-1" /> Nueva Terminal
                </>
              )}
            </Button>
          </div>
        </DialogHeader>

        {/* Resumen de estados y regla de historial */}
        <div className="pt-2 space-y-2">
          <div className="grid grid-cols-3 gap-2">
            <div className="bg-slate-50 border border-slate-200/80 rounded-lg p-2.5 text-center">
              <span className="text-[11px] font-medium text-slate-500 block">Total</span>
              <span className="text-lg font-bold text-slate-800">{terminals.length}</span>
            </div>
            <div className="bg-emerald-50/70 border border-emerald-200/60 rounded-lg p-2.5 text-center">
              <span className="text-[11px] font-medium text-emerald-700 block">Activas</span>
              <span className="text-lg font-bold text-emerald-700">{activasCount}</span>
            </div>
            <div className="bg-amber-50/70 border border-amber-200/60 rounded-lg p-2.5 text-center">
              <span className="text-[11px] font-medium text-amber-700 block">Bloqueadas</span>
              <span className="text-lg font-bold text-amber-700">{bloqueadasCount}</span>
            </div>
          </div>

          <div className="flex items-center gap-2 px-3 py-1.5 rounded-md bg-blue-50/60 border border-blue-100 text-[11px] text-blue-800">
            <History className="w-3.5 h-3.5 text-blue-600 shrink-0" />
            <span>
              <strong>Garantía histórica:</strong> Si eliminas o editas una terminal, cualquier lead o pago previamente cobrado conserva el dato de la terminal como historial permanente.
            </span>
          </div>
        </div>

        {/* Formulario Agregar Nueva Terminal */}
        {showAddForm && (
          <form onSubmit={handleCreate} className="bg-slate-50 border border-blue-200/80 rounded-xl p-4 space-y-3 mt-2 shadow-xs">
            <div className="flex items-center justify-between">
              <span className="text-xs font-bold text-blue-900 uppercase tracking-wider flex items-center gap-1.5">
                <Plus className="w-4 h-4 text-blue-600" /> Registrar Nueva Terminal
              </span>
              <button
                type="button"
                onClick={() => setShowAddForm(false)}
                className="text-slate-400 hover:text-slate-600"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label className="text-xs text-slate-700 font-medium">Nombre de la Terminal *</Label>
                <Input
                  placeholder="Ej. Terminal BBVA Mostrador 1"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="h-9 text-sm mt-1 bg-white"
                  required
                  autoFocus
                />
              </div>
              <div>
                <Label className="text-xs text-slate-700 font-medium">Estado Inicial</Label>
                <select
                  value={status}
                  onChange={(e) => setStatus(e.target.value)}
                  className="w-full h-9 mt-1 rounded-md border border-slate-200 bg-white px-3 text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="activa">Activa (Disponible para cobros)</option>
                  <option value="bloqueada">Bloqueada (Temporalmente inhabilitada)</option>
                </select>
              </div>
            </div>
            <div>
              <Label className="text-xs text-slate-700 font-medium">Notas o Ubicación (Opcional)</Label>
              <Input
                placeholder="Ej. Caja principal, mostrador VIP, terminal de respaldo..."
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                className="h-9 text-xs bg-white mt-1"
              />
            </div>
            <div className="flex justify-end gap-2 pt-1">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setShowAddForm(false)}
                className="text-xs"
              >
                Cancelar
              </Button>
              <Button
                type="submit"
                size="sm"
                disabled={submitting || !name.trim()}
                className="bg-blue-600 hover:bg-blue-700 text-white text-xs font-semibold"
              >
                {submitting ? (
                  <RefreshCw className="w-3.5 h-3.5 animate-spin mr-1" />
                ) : (
                  <Plus className="w-3.5 h-3.5 mr-1" />
                )}
                Guardar Terminal
              </Button>
            </div>
          </form>
        )}

        {/* Formulario Editar Terminal Existente */}
        {editingTerminal && (
          <form onSubmit={handleUpdate} className="bg-amber-50/50 border border-amber-300 rounded-xl p-4 space-y-3 mt-2 shadow-xs">
            <div className="flex items-center justify-between">
              <span className="text-xs font-bold text-amber-900 uppercase tracking-wider flex items-center gap-1.5">
                <Pencil className="w-4 h-4 text-amber-600" /> Editar Terminal: "{editingTerminal.name}"
              </span>
              <button
                type="button"
                onClick={cancelEdit}
                className="text-slate-400 hover:text-slate-600"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label className="text-xs text-slate-700 font-medium">Nombre de la Terminal *</Label>
                <Input
                  value={editName}
                  onChange={(e) => setEditName(e.target.value)}
                  className="h-9 text-sm mt-1 bg-white"
                  required
                  autoFocus
                />
              </div>
              <div>
                <Label className="text-xs text-slate-700 font-medium">Estado</Label>
                <select
                  value={editStatus}
                  onChange={(e) => setEditStatus(e.target.value)}
                  className="w-full h-9 mt-1 rounded-md border border-slate-200 bg-white px-3 text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-amber-500"
                >
                  <option value="activa">Activa (Disponible para cobros)</option>
                  <option value="bloqueada">Bloqueada (Temporalmente inhabilitada)</option>
                </select>
              </div>
            </div>
            <div>
              <Label className="text-xs text-slate-700 font-medium">Notas o Ubicación</Label>
              <Input
                value={editNotes}
                onChange={(e) => setEditNotes(e.target.value)}
                placeholder="Notas de soporte o ubicación"
                className="h-9 text-xs bg-white mt-1"
              />
            </div>
            <div className="flex justify-end gap-2 pt-1">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={cancelEdit}
                className="text-xs"
              >
                Cancelar
              </Button>
              <Button
                type="submit"
                size="sm"
                disabled={submitting || !editName.trim()}
                className="bg-amber-600 hover:bg-amber-700 text-white text-xs font-semibold"
              >
                {submitting ? (
                  <RefreshCw className="w-3.5 h-3.5 animate-spin mr-1" />
                ) : (
                  <Check className="w-3.5 h-3.5 mr-1" />
                )}
                Actualizar Cambios
              </Button>
            </div>
          </form>
        )}

        {/* Lista de Terminales */}
        <div className="flex-1 overflow-y-auto mt-3 pr-1 space-y-2.5 min-h-[220px]">
          <div className="flex items-center justify-between mb-1">
            <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">
              Terminales Registradas ({terminals.length})
            </span>
            {!showAddForm && !editingTerminal && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => setShowAddForm(true)}
                className="text-xs text-blue-600 hover:text-blue-700 h-7 px-2"
              >
                <Plus className="w-3.5 h-3.5 mr-1" /> Agregar Terminal
              </Button>
            )}
          </div>

          {loading ? (
            <div className="py-12 text-center text-xs text-slate-400 flex items-center justify-center gap-2">
              <RefreshCw className="w-4 h-4 animate-spin text-blue-500" />
              Cargando terminales...
            </div>
          ) : terminals.length === 0 ? (
            <div className="py-12 text-center text-xs text-slate-400 bg-slate-50 rounded-lg border border-dashed border-slate-200">
              No hay terminales registradas. Haz clic en "Nueva Terminal" para registrar la primera.
            </div>
          ) : (
            terminals.map((term) => {
              const isBlocked = term.status === "bloqueada";
              const isBeingEdited = editingTerminal?.id === term.id;
              return (
                <div
                  key={term.id}
                  className={`flex flex-col sm:flex-row sm:items-center justify-between p-3.5 rounded-xl border gap-3 transition-colors ${
                    isBeingEdited
                      ? "ring-2 ring-amber-500 bg-amber-50/30 border-amber-300"
                      : isBlocked
                      ? "bg-amber-50/30 border-amber-200/80"
                      : "bg-white border-slate-200/90 hover:border-slate-300 shadow-xs"
                  }`}
                >
                  <div className="flex items-start sm:items-center gap-3 min-w-0">
                    <div
                      className={`w-9 h-9 rounded-xl flex items-center justify-center shrink-0 ${
                        isBlocked
                          ? "bg-amber-100 text-amber-700 border border-amber-200"
                          : "bg-emerald-100 text-emerald-700 border border-emerald-200"
                      }`}
                    >
                      {isBlocked ? (
                        <Lock className="w-4 h-4" />
                      ) : (
                        <CreditCard className="w-4 h-4" />
                      )}
                    </div>
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-sm font-bold text-slate-900 truncate">
                          {term.name}
                        </span>
                        <span
                          className={`text-[10px] uppercase font-bold tracking-wider px-2 py-0.5 rounded-full inline-flex items-center gap-1 shrink-0 ${
                            isBlocked
                              ? "bg-amber-100 text-amber-800 border border-amber-300"
                              : "bg-emerald-100 text-emerald-800 border border-emerald-300"
                          }`}
                        >
                          {isBlocked ? (
                            <>
                              <AlertOctagon className="w-3 h-3 text-amber-600" /> Bloqueada
                            </>
                          ) : (
                            <>
                              <CheckCircle2 className="w-3 h-3 text-emerald-600" /> Activa
                            </>
                          )}
                        </span>
                      </div>
                      {term.notes ? (
                        <p className="text-xs text-slate-500 mt-0.5 truncate">{term.notes}</p>
                      ) : (
                        <p className="text-[11px] text-slate-400 mt-0.5 italic">Sin notas adicionales</p>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center gap-1.5 shrink-0 self-end sm:self-center">
                    {/* Botón Editar */}
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => startEdit(term)}
                      className="h-8 px-2.5 text-xs font-medium text-slate-700 hover:text-slate-900 hover:bg-slate-100 border-slate-200"
                      title="Editar nombre, estado y notas"
                    >
                      <Pencil className="w-3.5 h-3.5 mr-1 text-slate-500" /> Editar
                    </Button>

                    {/* Botón de cambio rápido de estado */}
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => handleToggleStatus(term)}
                      className={`h-8 px-2.5 text-xs font-medium ${
                        isBlocked
                          ? "text-emerald-700 border-emerald-300 hover:bg-emerald-50"
                          : "text-amber-700 border-amber-300 hover:bg-amber-50"
                      }`}
                      title={isBlocked ? "Reactivar terminal para cobros" : "Bloquear terminal temporalmente"}
                    >
                      {isBlocked ? (
                        <>
                          <Unlock className="w-3.5 h-3.5 mr-1" /> Desbloquear
                        </>
                      ) : (
                        <>
                          <Lock className="w-3.5 h-3.5 mr-1" /> Bloquear
                        </>
                      )}
                    </Button>

                    {/* Botón Eliminar terminal */}
                    <ConfirmDialog
                      title={`¿Eliminar terminal "${term.name}"?`}
                      description={`Esta terminal se quitará del catálogo activo. Los clientes, leads y cobros ya registrados conservarán el registro de "${term.name}" como dato histórico intacto.`}
                      onConfirm={() => handleDelete(term)}
                    >
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-slate-400 hover:text-rose-600 hover:bg-rose-50"
                        title="Eliminar del catálogo (conserva historial de cobros)"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </ConfirmDialog>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
